import axios from 'axios';

const INIT_FLAG = '__apiLoggerInitialized';

const normalizeMethod = (method) =>
  String(method || 'GET').toUpperCase();

const getFetchUrl = (input) => {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return '';
};

const buildAxiosUrl = (config = {}) => {
  const base = config.baseURL || '';
  const url = config.url || '';
  if (!url) return base;
  if (/^https?:\/\//i.test(url)) return url;
  if (!base) return url;
  if (url.startsWith('/')) return `${base}${url}`;
  return `${base}/${url}`;
};

const shouldLog = (url) => {
  const text = String(url || '');
  return text.includes('/api');
};

const summarizeBody = (body) => {
  if (body === undefined || body === null) return undefined;
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof FormData) return '[FormData]';
  if (body instanceof Blob || body instanceof ArrayBuffer) return '[Binary]';
  if (typeof body === 'object') {
    try {
      return JSON.stringify(body);
    } catch (error) {
      return '[Unserializable]';
    }
  }
  return String(body);
};

export const initApiLogger = () => {
  if (typeof window === 'undefined') return;
  if (window[INIT_FLAG]) return;
  window[INIT_FLAG] = true;

  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
      // Inject JWT Authorization header
      try {
        const token = localStorage.getItem('token');
        if (token) {
          init.headers = { ...init.headers, Authorization: `Bearer ${token}` };
        }
      } catch { /* localStorage may be unavailable during page transitions */ }

      const url = getFetchUrl(input);
      const method = normalizeMethod(init.method || input?.method);
      const body = summarizeBody(init.body);

      if (shouldLog(url)) {
        console.info('[API] fetch request', { method, url, body });
      }

      const response = await originalFetch(input, init);

      if (shouldLog(url)) {
        console.info('[API] fetch response', {
          method,
          url,
          status: response.status
        });
      }

      return response;
    };
  }

  axios.interceptors.request.use((config) => {
    // Inject JWT Authorization header for global axios
    try {
      const token = localStorage.getItem('token');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch { /* localStorage may be unavailable during page transitions */ }

    const url = buildAxiosUrl(config);
    if (shouldLog(url)) {
      console.info('[API] axios request', {
        method: normalizeMethod(config.method),
        url,
        params: config.params,
        data: config.data
      });
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const url = buildAxiosUrl(response?.config);
      if (shouldLog(url)) {
        console.info('[API] axios response', {
          method: normalizeMethod(response?.config?.method),
          url,
          status: response.status
        });
      }
      return response;
    },
    (error) => {
      const url = buildAxiosUrl(error?.config);
      if (shouldLog(url)) {
        console.info('[API] axios error', {
          method: normalizeMethod(error?.config?.method),
          url,
          status: error?.response?.status
        });
      }
      return Promise.reject(error);
    }
  );
};
